import { Stack, Typography, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import {
  DataTable,
  DownArrowIcon,
  PriorityDropdown,
  SlaStatusBadge,
  StatusDropdown,
} from "components";
import { useUpdateTicketPriority, useUpdateTicketStatus } from "controller";
import { toast } from "react-hot-toast";
import { TicketHeaders } from "shared";
import {
  calculateSlaStatus,
  convertTimeObjectToString,
  getFullName,
} from "utilz/helpers";
import CrownIcon from "assets/crown.svg";

export const TicketsTable = ({
  data,
  currentPageNumber,
  setCurrentPageNumber,
  itemsPerPage,
  setItemsPerPage,
  handleRowClick,
  isDataLoading,
}: {
  // biome-ignore lint/suspicious/noExplicitAny: legacy DataTable response shape
  data: any;
  currentPageNumber: number;
  setCurrentPageNumber: (currentPage: number) => void;
  itemsPerPage: number;
  setItemsPerPage: (perPage: number) => void;
  // biome-ignore lint/suspicious/noExplicitAny: legacy DataTable row callback
  handleRowClick: (rowData: any) => void;
  isDataLoading?: boolean;
}) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { mutateAsync: updatePriority } = useUpdateTicketPriority();
  const { mutateAsync: updateStatus } = useUpdateTicketStatus();
  const cellBuilders = {
    sla_rule: (rowData: Record<string, unknown>) => {
      // Use server-driven SLA tracking status if available
      const slaTracking = rowData.sla_tracking as
        | { overallStatus?: string }
        | null
        | undefined;

      if (slaTracking?.overallStatus) {
        return (
          <SlaStatusBadge
            showSlaText={false}
            type={slaTracking.overallStatus as "on_track" | "at_risk" | "breached" | "met" | "paused" | "pending"}
          />
        );
      }

      // Fallback to client-side calculation for backward compatibility
      const slaStatus = calculateSlaStatus({
        slaRules: rowData.sla_rules as Record<string, unknown>[],
        priority: rowData.priority as string,
        isSlaExpired: false,
        status: rowData?.ticket_status as string,
        createdAt: rowData?.created_at as string,
      });

      return (
        <SlaStatusBadge
          showSlaText={false}
          type={(slaStatus as "Open" | "Warning" | "Expired") || "Open"}
        />
      );
    },
    // biome-ignore lint/suspicious/noExplicitAny: legacy cellBuilder
    customer_user_first_name: (rowData: any) => {
      const fullname = getFullName(
        rowData?.customer_user_first_name,
        rowData?.customer_user_middle_name,
        rowData?.customer_user_last_name,
      );
      return (
        <Stack direction={"row"} sx={{ gap: "8px", alignItems: "center" }}>
          <Typography component={"span"} sx={{ textTransform: "capitalize" }}>
            {fullname}
          </Typography>
          {rowData?.is_privileged_user && (
            <img
              src={CrownIcon}
              alt="crown"
              style={{ width: "20px", height: "20px" }}
            />
          )}
        </Stack>
      );
    },
    // biome-ignore lint/suspicious/noExplicitAny: legacy cellBuilder
    budgeted_hours: (rowData: any) => {
      const budgetedHours =
        rowData?.budgeted_hours &&
        JSON.stringify(rowData?.budgeted_hours) !== "{}"
          ? convertTimeObjectToString(rowData?.budgeted_hours)
          : "";
      return <Typography textAlign={"center"}>{budgetedHours}</Typography>;
    },
    // biome-ignore lint/suspicious/noExplicitAny: legacy cellBuilder
    actual_hours: (rowData: any) => {
      const actualHours =
        rowData?.actual_hours && JSON.stringify(rowData?.actual_hours) !== "{}"
          ? convertTimeObjectToString(rowData?.actual_hours)
          : "";
      return <Typography textAlign={"center"}>{actualHours}</Typography>;
    },
    // biome-ignore lint/suspicious/noExplicitAny: legacy cellBuilder
    priority: (rowData: any) => {
      return (
        <Stack direction={"row"} onClick={(e) => e.stopPropagation()}>
          <PriorityDropdown
            isDisabled={
              rowData?.ticket_status === "da5be1f9-db9d-4c64-8e43-51b458746773"
            }
            priority={rowData.priority}
            onSelect={async (value) => {
              try {
                await updatePriority({
                  ticketId: rowData?.mapping_id,
                  priority: value,
                });
                queryClient.invalidateQueries(["tickets"]);
                toast.success("Priority updated successfully");
              } catch {
                toast.error("Unable to update priority");
              }
            }}
            endIcon={
              rowData?.ticket_status ===
              "da5be1f9-db9d-4c64-8e43-51b458746773" ? null : (
                <DownArrowIcon htmlColor={theme.palette.lightBg.high} />
              )
            }
          />
        </Stack>
      );
    },

    // biome-ignore lint/suspicious/noExplicitAny: legacy cellBuilder
    status_name: (rowData: any) => {
      const statusName = rowData?.status_name || "";
      const truncatedStatusName =
        statusName && statusName.length > 10
          ? `${statusName.slice(0, 10)}...`
          : statusName;
      return (
        <Stack direction={"row"} onClick={(e) => e.stopPropagation()}>
          <StatusDropdown
            serviceType="ticket"
            isDisabled={
              rowData?.ticket_status === "da5be1f9-db9d-4c64-8e43-51b458746773"
            }
            value={truncatedStatusName || ""}
            status={rowData.ticket_status}
            onSelect={async (value) => {
              try {
                await updateStatus({
                  ticketId: rowData?.mapping_id,
                  statusId: value,
                });
                toast.success("Status updated successfully");
                queryClient.invalidateQueries(["tickets"]);
              } catch {
                toast.error("Unable to update status");
              }
            }}
            endIcon={
              rowData?.ticket_status ===
              "da5be1f9-db9d-4c64-8e43-51b458746773" ? null : (
                <DownArrowIcon htmlColor={theme.palette.lightBg.high} />
              )
            }
          />
        </Stack>
      );
    },
  };
  return (
    <DataTable
      isDataLoading={isDataLoading}
      multiselect={false}
      handleRowClick={handleRowClick}
      currentPageNumber={currentPageNumber}
      itemsPerPage={itemsPerPage}
      onPerPageChange={(newItemsPerPage) => setItemsPerPage(newItemsPerPage)}
      totalPages={data?.totalPages}
      totalItems={data?.totalCount}
      onPagination={(d) => setCurrentPageNumber(d)}
      showPagination={true}
      headers={TicketHeaders}
      data={data?.result}
      selectedItems={[]}
      cellBuilders={cellBuilders}
      pageNationStyles={{ px: "16px" }}
      sx={{
        height: {
          xs: "calc(100vh - 150px)",
          sm: "calc(100vh - 150px)",
          md: "calc(100vh - 150px)",
          lg: "calc(100vh - 150px)",
        },
      }}
    />
  );
};
